import { useRef, useState } from "react";
import { Search, Upload, ImageOff, Crop } from "lucide-react";
import { toast } from "sonner";
import { assetUrl, uploadFile, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CropDialog } from "@/components/builder/CropDialog";
import { LibraryImage, useLibraryImages, uploadLibraryImages, filterImages } from "@/lib/libraryImages";

interface Props { open: boolean; onClose: () => void; onPick: (img: LibraryImage) => void; title?: string }

export function ImagePickerDialog({ open, onClose, onPick, title = "Choose from image library" }: Props) {
  const { images, tags, loading, reload } = useLibraryImages(open);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cropping, setCropping] = useState<LibraryImage | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const shown = filterImages(images, q, tag);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try { if (await uploadLibraryImages(Array.from(files), tag ? [tag] : [])) await reload(); } finally { setBusy(false); }
  };

  const cropDone = async (blob: Blob) => {
    if (!cropping) return;
    try {
      const r = await uploadFile(new File([blob], `${cropping.name}-cropped.jpg`, { type: "image/jpeg" }), "library-crops");
      onPick({ ...cropping, id: r.id, file_id: r.id, url: r.url, name: `${cropping.name} (cropped)` });
      toast.success("Cropped image ready"); setCropping(null); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-4xl" data-testid="image-picker-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]"><Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" /><Input data-testid="image-picker-search" className="pl-8" placeholder="Search by name or tag…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <Button size="sm" variant="outline" disabled={busy} data-testid="image-picker-upload-btn" onClick={() => fileRef.current?.click()}><Upload size={13} className="mr-1.5" />{busy ? "Uploading…" : "Upload to library"}</Button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="image-picker-tags">
            <button type="button" data-testid="image-picker-tag-all" onClick={() => setTag(null)} className={`rounded-full px-2.5 py-0.5 text-xs ${!tag ? "bg-blue-700 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>All</button>
            {tags.map((t) => <button key={t} type="button" data-testid={`image-picker-tag-${t}`} onClick={() => setTag(tag === t ? null : t)} className={`rounded-full px-2.5 py-0.5 text-xs ${tag === t ? "bg-blue-700 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{t}</button>)}
          </div>
        )}
        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {shown.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-center text-sm text-gray-500" data-testid="image-picker-empty"><ImageOff size={28} className="text-gray-300" />{loading ? "Loading…" : images.length ? "No images match." : "The library is empty — upload your first image."}</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {shown.map((im) => (
                <div key={im.id} className="group relative overflow-hidden rounded-lg border border-gray-200 transition-shadow hover:shadow-md hover:ring-2 hover:ring-blue-500" data-testid={`image-picker-item-wrap-${im.id}`}>
                  <button type="button" data-testid={`image-picker-item-${im.id}`} onClick={() => { onPick(im); onClose(); }} className="block w-full text-left">
                    <img src={assetUrl(im.url) || ""} alt={im.name} className="aspect-[4/3] w-full object-cover transition-transform group-hover:scale-[1.03]" loading="lazy" />
                    <div className="px-2 py-1.5"><div className="truncate text-xs font-medium text-gray-900">{im.name}</div>{im.tags.length > 0 && <div className="truncate text-[10px] text-gray-500">{im.tags.join(", ")}</div>}</div>
                  </button>
                  <button type="button" title="Crop / straighten before using" data-testid={`image-picker-crop-${im.id}`} onClick={(e) => { e.stopPropagation(); setCropping(im); }} className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded bg-white/90 px-1.5 py-1 text-[10px] font-medium text-gray-800 shadow opacity-0 transition-opacity group-hover:opacity-100"><Crop size={11} />Crop</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {cropping && <CropDialog open imageUrl={assetUrl(cropping.url)!} onClose={() => setCropping(null)} onDone={cropDone}
          simple={{ title: `Crop & straighten “${cropping.name}”`, description: "Frame the image the way you want it in the proposal. The library keeps the original untouched.", confirmLabel: "Use cropped image" }} />}
      </DialogContent>
    </Dialog>
  );
}
